import React, { useMemo, useEffect, useState } from 'react';
import { Modal, Box, Typography } from '@mui/material';
import refresh from "packages/novous_web/src/assets/icons/Refresh.svg";
import arrowUp from "packages/novous_web/src/assets/icons/ArrowBlack.svg";
import arrowDown from "packages/novous_web/src/assets/icons/ArrowDownBlack.svg";
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';

interface SubscriptionFilterModalProps {
    open: boolean;
    onClose: () => void;
    onReset?: () => void;
    title?: string;
    isClear?: boolean;
    subTitle?: string;
    handleFilterSave: (data: any) => void;
    description?: string;
    showClearButton?: boolean;
    subscriptionData?: any[]; // Pass the subscription data from parent
    initialFilters?: {
        // userName?: string | null;
        status?: string | null;
        productItem?: string | null;
    };
}

interface FilterOption {
    value: string;
    label: string;
}

const SubscriptionFilterModal: React.FC<SubscriptionFilterModalProps> = ({
    open,
    onClose,
    onReset,
    isClear = false,
    title = 'Filter Subscriptions',
    subTitle = '',
    handleFilterSave,
    description = 'Filter subscriptions by customer name, status, or subscription type.',
    showClearButton = true,
    subscriptionData = [],
    initialFilters,
}) => {
    const [clear, setClear] = useState<boolean>(isClear || false);
    const { user } = useAuth();

    // Selections
    // const [selectedUserName, setSelectedUserName] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [selectedProductItem, setSelectedProductItem] = useState<string | null>(null);

    // Expanded/collapsed states
    // const [userNameExpanded, setUserNameExpanded] = useState<boolean>(false);
    const [statusExpanded, setStatusExpanded] = useState<boolean>(false);
    const [productItemExpanded, setProductItemExpanded] = useState<boolean>(false);

    const {
        data: productItems,
        error: productItemsError,
        loading: productItemsLoading,
        fetchData: fetchproductItemsData,
    } = useAxios<any>(`${API_ENDPOINTS.clientSubscription.PRODUCT_ITEMS}`, "GET");

    useEffect(() => {
        if (user?.authorities === 'ROLE_ADMIN' || user?.authorities === 'ROLE_SUPER_ADMIN') {
            fetchproductItemsData();
        }
    }, [user])

    const productItemOptions = productItems?.data?.map((item: any) => ({
        label: item.name,
        value: item.name
    })) || [];

    const filterOptions = useMemo(() => {
        if (!subscriptionData || subscriptionData.length === 0) {
            return {
                // userNames: [],
                statuses: [],
                productItems: []
            };
        }

        // const uniqueUserNames = Array.from(new Set(
        //     subscriptionData
        //         .map(item => item.userName)
        //         .filter(Boolean)
        // )).map(name => ({ value: name, label: name }));

        const uniqueStatuses: FilterOption[] = [
            { value: 'active', label: 'Active' },
            { value: 'expired', label: 'Expired' },
            { value: 'expiring_soon', label: 'Expiring Soon' },
            { value: 'terminated', label: 'Terminated' }
        ];

        const productItemsArray = productItemOptions;
        const uniqueProductItems: FilterOption[] = productItemsArray;

        return {
            // userNames: uniqueUserNames,
            statuses: uniqueStatuses,
            productItems: uniqueProductItems
        };
    }, [subscriptionData]);

    useEffect(() => {
        setClear(isClear);
    }, [isClear]);

    useEffect(() => {
        if (clear) {
            // setSelectedUserName(null);
            setSelectedStatus(null);
            setSelectedProductItem(null);
            // setUserNameExpanded(false);
            setStatusExpanded(false);
            setProductItemExpanded(false);
            setClear(false);
            onReset?.();
        }
    }, [clear, onReset]);

    // Handlers
    // const handleUserNameChange = (newValue: string) => {
    //     setSelectedUserName(newValue);
    // };

    const handleStatusChange = (newValue: string) => {
        setSelectedStatus(newValue);
    };

    const handleProductItemChange = (newValue: string) => {
        setSelectedProductItem(newValue);
    };

    // const toggleUserNameExpanded = () => {
    //     setUserNameExpanded((prev) => !prev);
    // };

    const toggleStatusExpanded = () => {
        setStatusExpanded((prev) => !prev);
    };

    const toggleProductItemExpanded = () => {
        setProductItemExpanded((prev) => !prev);
    };

    const handleReset = () => {
        // setSelectedUserName(null);
        setSelectedStatus(null);
        setSelectedProductItem(null);
        // setUserNameExpanded(false);
        setStatusExpanded(false);
        setProductItemExpanded(false);
        onReset?.();
    };

    const handleSave = () => {
        handleFilterSave({
            // userName: selectedUserName,
            status: selectedStatus,
            productItem: selectedProductItem,
        });
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    // Initialize selections from initialFilters when opening
    useEffect(() => {
        if (open) {
            if (initialFilters) {
                // setSelectedUserName(initialFilters.userName ?? null);
                setSelectedStatus(initialFilters.status ?? null);
                setSelectedProductItem(initialFilters.productItem ?? null);
            }
        }
    }, [open, initialFilters]);

    const hasChanges = useMemo(() => {
        const baseline = initialFilters || {};
        const eq = (a: string | null | undefined, b: string | null | undefined) =>
            (a ?? null) === (b ?? null);

        return !(
            // eq(selectedUserName, baseline.userName) &&
            eq(selectedStatus, baseline.status) &&
            eq(selectedProductItem, baseline.productItem)
        );
    }, [selectedStatus, selectedProductItem, initialFilters]);

    return (
        <Modal
            open={open}
            onClose={handleCancel}
            aria-labelledby="subscription-filter-title"
            aria-describedby="subscription-filter-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '70%',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    overflowY: 'auto',
                    height: '80vh'
                }}
            >
                <div className="flex justify-between h-full flex-col">
                    <div className="flex gap-6 flex-col">
                        {/* Header */}
                        <div className="flex flex-col gap-1">
                            <div className="flex justify-between items-center">
                                <Typography variant="h6">{title} {subTitle}</Typography>
                                {showClearButton && (
                                    <CustomIconButton
                                        text="Clear All"
                                        icon={<img src={refresh} alt="reset" />}
                                        background="transparent"
                                        variant="outlined"
                                        onClick={handleReset}
                                    />
                                )}
                            </div>
                            {description && (
                                <Typography variant="button" fontWeight={400} color="secondary">
                                    {description}
                                </Typography>
                            )}
                        </div>

                        {/* Customer Name section */}
                        {/* <div className="flex flex-col gap-2">
                            <div
                                className="flex justify-between items-center cursor-pointer"
                                onClick={toggleUserNameExpanded}
                            >
                                <Typography variant="body1" fontWeight={600}>
                                    Customer Name
                                </Typography>
                                <img
                                    src={userNameExpanded ? arrowUp : arrowDown}
                                    width={24}
                                    height={24}
                                    alt={userNameExpanded ? 'collapse' : 'expand'}
                                />
                            </div>
                            {userNameExpanded && (
                                <div className='max-w-full'>
                                    <CustomDropDownInput
                                        options={filterOptions.userNames}
                                        value={selectedUserName || ''}
                                        onChange={handleUserNameChange}
                                        placeholder="Select Customer Name"
                                    />
                                </div>
                            )}
                        </div> */}

                        <hr />

                        {/* Status section */}
                        <div className="flex flex-col gap-2">
                            <div
                                className="flex justify-between items-center cursor-pointer"
                                onClick={toggleStatusExpanded}
                            >
                                <Typography variant="body1" fontWeight={600}>
                                    Status
                                </Typography>
                                <img
                                    src={statusExpanded ? arrowUp : arrowDown}
                                    width={24}
                                    height={24}
                                    alt={statusExpanded ? 'collapse' : 'expand'}
                                />
                            </div>
                            {statusExpanded && (
                                <div className='max-w-full'>
                                    <CustomDropDownInput
                                        options={filterOptions.statuses}
                                        value={selectedStatus || ''}
                                        onChange={handleStatusChange}
                                        placeholder="Select Status"
                                    />
                                </div>
                            )}
                        </div>

                        <hr />

                        {/* Subscription Type section */}
                        <div className="flex flex-col gap-2">
                            <div
                                className="flex justify-between items-center cursor-pointer"
                                onClick={toggleProductItemExpanded}
                            >
                                <Typography variant="body1" fontWeight={600}>
                                    Subscription Type
                                </Typography>
                                <img
                                    src={productItemExpanded ? arrowUp : arrowDown}
                                    width={24}
                                    height={24}
                                    alt={productItemExpanded ? 'collapse' : 'expand'}
                                />
                            </div>
                            {productItemExpanded && (
                                <div className='max-w-full'>
                                    <CustomDropDownInput
                                        options={filterOptions.productItems}
                                        value={selectedProductItem || ''}
                                        onChange={handleProductItemChange}
                                        placeholder="Select Subscription Type"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-6 items-center justify-between">
                        <CustomIconButton
                            text="Cancel"
                            background="transparent"
                            outline
                            padding="8px 16px"
                            variant="outlined"
                            onClick={handleCancel}
                        />
                        <CustomIconButton
                            text="Apply Filters"
                            outline
                            padding="8px 16px"
                            variant="contained"
                            isDisabled={!hasChanges}
                            onClick={handleSave}
                        />
                    </div>
                </div>
            </Box>
        </Modal>
    );
};

export default SubscriptionFilterModal;